import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentsAPI } from '../utils/api';
import CameraCapture from '../components/CameraCapture';
import PikiLogo from '../components/PikiLogo';

const COUNTIES = [
  'Baringo','Bomet','Bungoma','Busia','Elgeyo-Marakwet','Embu','Garissa',
  'Homa Bay','Isiolo','Kajiado','Kakamega','Kericho','Kiambu','Kilifi',
  'Kirinyaga','Kisii','Kisumu','Kitui','Kwale','Laikipia','Lamu','Machakos',
  'Makueni','Mandera','Marsabit','Meru','Migori','Mombasa','Murang\'a',
  'Nairobi','Nakuru','Nandi','Narok','Nyamira','Nyandarua','Nyeri','Samburu',
  'Siaya','Taita-Taveta','Tana River','Tharaka-Nithi','Trans-Nzoia','Turkana',
  'Uasin Gishu','Vihiga','Wajir','West Pokot',
];

// Uses /upload-kyc (no auth required) so uploading during registration never triggers logout
function KycUploader({ label, docType, required, onUploaded, tempId, uploaded }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [drag, setDrag] = useState(false);
  const ref = useRef();

  const upload = async (f) => {
    if (!f) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('files', f);
      fd.append('docType', docType);
      fd.append('tempUploadId', tempId);
      const res = await documentsAPI.uploadKyc(fd);  // ← no-auth endpoint
      onUploaded(docType, res.data.documents[0]);
    } catch (e) { setError(e.response?.data?.error || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const isUploaded = !!uploaded;

  return (
    <div style={{
      border: `1.5px ${isUploaded?'solid':'dashed'} ${isUploaded?'var(--green)':drag?'var(--green)':'var(--border)'}`,
      borderRadius: 8, padding:'10px 12px',
      background: isUploaded ? 'var(--green-light)' : drag ? '#F0FDF4' : '#FAFAFA',
      transition: 'all .2s',
    }}
      onDragOver={e=>{e.preventDefault();setDrag(true);}}
      onDragLeave={()=>setDrag(false)}
      onDrop={e=>{e.preventDefault();setDrag(false);upload(e.dataTransfer.files[0]);}}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
            <span style={{ fontSize:12, fontWeight:600 }}>{label}</span>
            {required && !isUploaded && <span style={{ fontSize:9, background:'#FEF2F2', color:'var(--red)', padding:'1px 5px', borderRadius:3, fontWeight:700 }}>REQUIRED</span>}
            {isUploaded && <span style={{ fontSize:9, background:'var(--green-light)', color:'var(--green)', padding:'1px 5px', borderRadius:3, fontWeight:700 }}>✓ UPLOADED</span>}
          </div>
          {isUploaded
            ? <div style={{ fontSize:11, color:'var(--green)' }}>📄 {uploaded.originalName} · {(uploaded.size/1024).toFixed(0)} KB</div>
            : <div style={{ fontSize:11, color:'var(--muted)' }}>JPG, PNG or PDF · drag, browse or take photo</div>
          }
          {error && <div style={{ fontSize:11, color:'var(--red)', marginTop:2 }}>⚠️ {error}</div>}
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:5, flexShrink:0 }}>
          <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,image/*" style={{ display:'none' }}
            onChange={e=>upload(e.target.files[0])}/>
          {isUploaded ? (
            <button type="button" onClick={()=>ref.current?.click()} disabled={uploading}
              style={{ padding:'5px 10px', borderRadius:6, border:'1.5px solid var(--border)', background:'white', fontSize:11, fontWeight:600, cursor:'pointer', opacity:uploading ? 0.6 : 1 }}>
              ↺ Replace
            </button>
          ) : (
            <>
              <button type="button" onClick={()=>ref.current?.click()} disabled={uploading}
                style={{ padding:'5px 10px', borderRadius:6, border:'1.5px solid var(--border)', background:'white', fontSize:11, fontWeight:600, cursor:'pointer', opacity:uploading ? 0.6 : 1, whiteSpace:'nowrap' }}>
                {uploading ? '⏳ Uploading…' : '📁 Browse'}
              </button>
              <CameraCapture disabled={uploading} color="var(--green)" onCapture={f=>upload(f)}/>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const STEPS = ['Account','Rider Info','Documents'];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [kycDocs, setKycDocs] = useState({});
  const tempId = useRef(`reg-${Date.now()}`).current;

  const [form, setForm] = useState({
    fullName:'', phone:'', email:'', password:'', confirmPassword:'',
    nationalId:'', county:'Nairobi', licenseNumber:'', bikeReg:'', bikeType:'Petrol',
    isBikeOwner: true, ownerName:'', ownerPhone:'',
  });
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUploaded = (docType, doc) => setKycDocs(prev => ({ ...prev, [docType]: doc }));

  const next = (e) => {
    e.preventDefault();
    if (step === 1) {
      if (!form.fullName.trim()) return setError('Full name is required');
      if (!form.phone.trim()) return setError('Phone number is required');
      if (form.password !== form.confirmPassword) return setError('Passwords do not match');
      if (form.password.length < 8) return setError('Password must be at least 8 characters');
      setError(''); setStep(2);
    } else if (step === 2) {
      if (!form.nationalId) return setError('National ID is required');
      if (!form.licenseNumber) return setError("Rider's license number is required");
      if (!form.bikeReg) return setError('Bike registration is required');
      if (!form.isBikeOwner && !form.ownerName.trim()) return setError("Enter bike owner's name");
      if (!form.isBikeOwner && !form.ownerPhone.trim()) return setError("Enter bike owner's phone number");
      setError(''); setStep(3);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Enforce all 3 required docs
    const missing = [];
    if (!kycDocs.national_id) missing.push('National ID');
    if (!kycDocs.riders_license) missing.push("Rider's License");
    if (!kycDocs.insurance_cert) missing.push('Bike Insurance Certificate');
    if (missing.length) return setError(`Please upload: ${missing.join(', ')}`);

    setError(''); setLoading(true);
    try {
      await register({ ...form, tempUploadId: tempId });
      // Attach KYC docs to the new user account
      try { await documentsAPI.attachKyc(tempId); } catch {}
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-layout">
      {/* Left hero */}
      <div className="auth-hero">
        <div><PikiLogo size={36} textSize={17} light /></div>
        <div>
          <h1 style={{ fontSize:34, fontWeight:800, color:'white', lineHeight:1.15, marginBottom:14, letterSpacing:'-0.5px' }}>
            Join <span style={{ color:'#22C55E' }}>50,000+</span><br/>protected riders
          </h1>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {['Complete KYC in under 5 minutes','Protection starts same day','Claims via M-Pesa in 4 hours','Earn Shield Tokens for safe riding'].map(f => (
              <div key={f} style={{ display:'flex', gap:8, color:'rgba(255,255,255,.7)', fontSize:13 }}>
                <span style={{ color:'#22C55E', fontWeight:700, flexShrink:0 }}>✓</span>{f}
              </div>
            ))}
          </div>
        </div>
        <div style={{ background:'rgba(34,197,94,.1)', border:'1px solid rgba(34,197,94,.25)', borderRadius:10, padding:14 }}>
          <p style={{ color:'rgba(255,255,255,.6)', fontSize:12, lineHeight:1.6 }}>
            🔒 Data protected with AES-256 encryption and verified against NTSA records.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="auth-form-panel">
        <div className="auth-form-inner">
          {/* Step bar */}
          <div style={{ display:'flex', gap:6, marginBottom:24 }}>
            {STEPS.map((s, i) => (
              <div key={s} style={{ flex:1 }}>
                <div style={{ height:3, borderRadius:2, background: step>i+1?'var(--green)':step===i+1?'var(--green-mid)':'var(--border)', transition:'background .3s' }}/>
                <div style={{ fontSize:10, marginTop:3, fontWeight:step===i+1?700:400, color:step===i+1?'var(--text)':'var(--muted)' }}>{s}</div>
              </div>
            ))}
          </div>

          <h2 style={{ fontSize:22, fontWeight:800, marginBottom:2 }}>
            {step===1?'Create Your Account':step===2?'Rider Details':'Upload Documents'}
          </h2>
          <p className="text-muted" style={{ marginBottom:18, fontSize:12 }}>
            {step===1?'Step 1 of 3 — Personal information':step===2?'Step 2 of 3 — KYC & bike details':'Step 3 of 3 — All 3 documents required'}
          </p>

          {error && <div className="alert alert-error">{error}</div>}

          {/* Step 1 */}
          {step === 1 && (
            <form onSubmit={next}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" placeholder="James Mwangi" value={form.fullName} onChange={e=>up('fullName',e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number (M-Pesa)</label>
                <input className="form-input" placeholder="+254712345678" value={form.phone} onChange={e=>up('phone',e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Email <span style={{ color:'var(--muted)', fontWeight:400 }}>(optional)</span></label>
                <input className="form-input" type="email" placeholder="james@email.com" value={form.email} onChange={e=>up('email',e.target.value)} />
              </div>
              <div className="two-col">
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input className="form-input" type="password" placeholder="Min 8 chars" value={form.password} onChange={e=>up('password',e.target.value)} required minLength={8}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input className="form-input" type="password" placeholder="Repeat" value={form.confirmPassword} onChange={e=>up('confirmPassword',e.target.value)} required />
                </div>
              </div>
              <button className="btn btn-primary btn-lg" style={{ width:'100%', justifyContent:'center' }} type="submit">Continue →</button>
            </form>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <form onSubmit={next}>
              <div className="two-col">
                <div className="form-group">
                  <label className="form-label">National ID Number</label>
                  <input className="form-input" placeholder="12345678" value={form.nationalId} onChange={e=>up('nationalId',e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label">County</label>
                  <select className="form-input" value={form.county} onChange={e=>up('county',e.target.value)}>
                    {COUNTIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Rider's License No. *</label>
                  <input className="form-input" placeholder="DL-NBI-2021-..." value={form.licenseNumber} onChange={e=>up('licenseNumber',e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Bike Registration No. *</label>
                  <input className="form-input" placeholder="KCZ 123A" value={form.bikeReg} onChange={e=>up('bikeReg',e.target.value)} required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Bike Type</label>
                <div style={{ display:'flex', gap:8 }}>
                  {['Petrol','EV','Hybrid'].map(t=>(
                    <button key={t} type="button" onClick={()=>up('bikeType',t)}
                      style={{ flex:1, padding:'8px 0', borderRadius:8, border:form.bikeType===t?'2px solid var(--green)':'1.5px solid var(--border)', background:form.bikeType===t?'var(--green-light)':'white', fontWeight:600, fontSize:13, cursor:'pointer', color:form.bikeType===t?'var(--green)':'var(--text)' }}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bike ownership */}
              <div className="form-group">
                <label className="form-label">Are you the registered owner of this bike?</label>
                <div style={{ display:'flex', gap:8 }}>
                  {[['yes',true,'Yes, I own it'],['no',false,"No, it's not mine"]].map(([key,val,lbl])=>(
                    <button key={key} type="button" onClick={()=>up('isBikeOwner',val)}
                      style={{ flex:1, padding:'8px 12px', borderRadius:8, border:form.isBikeOwner===val?'2px solid var(--green)':'1.5px solid var(--border)', background:form.isBikeOwner===val?'var(--green-light)':'white', fontWeight:600, fontSize:12, cursor:'pointer', color:form.isBikeOwner===val?'var(--green)':'var(--text)' }}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {!form.isBikeOwner && (
                <div className="two-col" style={{ background:'#FFF8E1', border:'1px solid #FDE68A', borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Bike Owner's Full Name *</label>
                    <input className="form-input" placeholder="Owner name" value={form.ownerName} onChange={e=>up('ownerName',e.target.value)} required />
                  </div>
                  <div className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">Owner's Phone Number *</label>
                    <input className="form-input" placeholder="+254..." value={form.ownerPhone} onChange={e=>up('ownerPhone',e.target.value)} required />
                  </div>
                </div>
              )}

              <div style={{ display:'flex', gap:10 }}>
                <button className="btn btn-secondary" type="button" onClick={()=>setStep(1)}>← Back</button>
                <button className="btn btn-primary btn-lg" style={{ flex:1, justifyContent:'center' }} type="submit">Continue →</button>
              </div>
            </form>
          )}

          {/* Step 3 — Documents */}
          {step === 3 && (
            <form onSubmit={handleSubmit}>
              <div style={{ background:'#FFF8E1', border:'1px solid #FDE68A', borderRadius:8, padding:'10px 12px', marginBottom:14, fontSize:12 }}>
                ⚠️ All 3 documents below are <strong>required</strong> before your account can be activated.
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>
                <KycUploader label="National ID (Front & Back)" docType="national_id" required onUploaded={handleUploaded} tempId={tempId} uploaded={kycDocs.national_id}/>
                <KycUploader label="Rider's License" docType="riders_license" required onUploaded={handleUploaded} tempId={tempId} uploaded={kycDocs.riders_license}/>
                <KycUploader label="Bike Insurance Certificate" docType="insurance_cert" required onUploaded={handleUploaded} tempId={tempId} uploaded={kycDocs.insurance_cert}/>
              </div>

              {/* Progress indicator */}
              {(() => {
                const done = [kycDocs.national_id, kycDocs.riders_license, kycDocs.insurance_cert].filter(Boolean).length;
                return (
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
                    <div style={{ flex:1, height:4, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
                      <div style={{ width:`${(done/3)*100}%`, height:'100%', background: done===3?'var(--green)':'var(--orange)', borderRadius:2, transition:'width .3s' }}/>
                    </div>
                    <span style={{ fontSize:11, fontWeight:600, color: done===3?'var(--green)':'var(--muted)', whiteSpace:'nowrap' }}>
                      {done}/3 uploaded{done===3?' ✓':''}
                    </span>
                  </div>
                );
              })()}

              <div style={{ display:'flex', gap:10 }}>
                <button className="btn btn-secondary" type="button" onClick={()=>setStep(2)}>← Back</button>
                <button className="btn btn-primary btn-lg" style={{ flex:1, justifyContent:'center' }} type="submit" disabled={loading}>
                  {loading ? '⏳ Creating account…' : '🛡️ Create My Account'}
                </button>
              </div>
            </form>
          )}

          <p style={{ textAlign:'center', marginTop:18, fontSize:12, color:'var(--muted)' }}>
            Already have an account? <Link to="/login" style={{ color:'var(--green)', fontWeight:600, textDecoration:'none' }}>Sign in →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
